import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    ActivatedRouteSnapshot,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface PageVisitPayload {
    sessionId: string;
    url: string;
    route: string;
    urlWithoutQuery: string;
    fullUrl: string;
    referrer: string | null;
    userAgent: string;
    pageTitle: string | null;
}

const SESSION_STORAGE_KEY = 'lic_page_tracking_session';
const API_PAGE_VISIT_URL = environment.API_BASE_URL + '/api/PageVisit';

// Routes that should NOT be logged (e.g. the analytics dashboard itself).
// Matched by `urlWithoutQuery.pathname.startsWith(prefix)`.
const SKIP_ROUTES: string[] = [
    '/pages/admin/page-visits',
];

/**
 * Listens to Angular router navigation events and posts a page-visit record to the API.
 *
 * - Runs once, bootstrapped from AppComponent.
 * - Fire-and-forget — tracking never blocks or fails a user navigation.
 * - Attaches the JWT (if present) so the backend can resolve the user; otherwise stored as anonymous.
 * - Controlled by `environment.pageTracking.enabled` so it can be switched off per build.
 */
@Injectable({
    providedIn: 'root',
})
export class PageTrackingService {
    private started = false;
    private previousFullUrl: string | null = null;

    constructor(
        private readonly http: HttpClient,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute
    ) { }

    startTracking(): void {
        if (this.started) return;
        if (!this.isEnabled()) return;
        this.started = true;

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                try {
                    this.logVisit(event.urlAfterRedirects || event.url);
                } catch {
                    // swallow — tracking failures must never impact UX
                }
            });
    }

    private isEnabled(): boolean {
        const flag =
            environment && (environment as any).pageTracking
                ? (environment as any).pageTracking.enabled
                : false;
        return !!flag;
    }

    private logVisit(navigatedUrl: string): void {
        const pathname = window.location.pathname || '';
        // Skip configured routes (e.g. the analytics dashboard itself — we don't want to log opening it).
        if (SKIP_ROUTES.some((prefix) => pathname.startsWith(prefix))) {
            return;
        }

        const fullUrl = window.location.href;
        const urlWithoutQuery = window.location.origin + window.location.pathname;
        const payload: PageVisitPayload = {
            sessionId: this.getOrCreateSessionId(),
            url: window.location.origin,
            route: this.resolveRoutePattern(this.activatedRoute.snapshot) || navigatedUrl,
            urlWithoutQuery,
            fullUrl,
            referrer: this.previousFullUrl,
            userAgent: navigator.userAgent || '',
            pageTitle: document.title || null,
        };

        this.previousFullUrl = fullUrl;

        const headers = this.buildHeaders();

        this.http
            .post(API_PAGE_VISIT_URL, payload, { headers })
            .subscribe({
                next: () => { },
                error: () => {
                    // silent — never notify the user about tracking failures
                },
            });
    }

    /**
     * Walk the ActivatedRoute tree from the root to the deepest activated child
     * and assemble the route *pattern* (with param placeholders like `:id`)
     * instead of resolved values. Example result:
     *   "/pages/admin/lab-job-detail-pending/:serviceId/:laboratoryId"
     */
    private resolveRoutePattern(snapshot: ActivatedRouteSnapshot): string {
        const segments: string[] = [];
        let current: ActivatedRouteSnapshot | null = snapshot.root;
        while (current) {
            const path = current.routeConfig && current.routeConfig.path;
            if (path) segments.push(path);
            current = current.firstChild;
        }
        const joined = segments.filter((s) => s.length > 0).join('/');
        return joined ? '/' + joined : '/';
    }

    private getOrCreateSessionId(): string {
        try {
            let existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (!existing) {
                existing = this.generateUuid();
                sessionStorage.setItem(SESSION_STORAGE_KEY, existing);
            }
            return existing;
        } catch {
            // sessionStorage may be unavailable (private mode, SSR) — fallback to ephemeral id
            return this.generateUuid();
        }
    }

    private generateUuid(): string {
        const cryptoObj: any = (window as any).crypto;
        if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
            return cryptoObj.randomUUID();
        }
        // RFC4122-ish fallback
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    private buildHeaders(): HttpHeaders {
        let headers = new HttpHeaders().set('Content-Type', 'application/json');
        try {
            const token = localStorage.getItem(environment.authTokenKey);
            if (token) {
                headers = headers.set('Authorization', `Bearer ${token}`);
            }
        } catch {
            // localStorage may be unavailable — skip auth header
        }
        return headers;
    }
}
